import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from 'react-oidc-context';
import BookUploadPage from './pages/BookUploadPage';
import BookReaderPage from './pages/BookReaderPage';
import HomePage from './pages/HomePage';
import CallbackPage from './pages/CallbackPage';
import BulkLicenseAssignmentPage from './pages/BulkLicenseAssignmentPage';
import MyLibraryPage from './pages/MyLibraryPage';
import StudentQuotasPage from './pages/StudentQuotasPage';
import SearchResultsPage from './pages/SearchResultsPage';
import BooksellerAuthTestPage from './pages/admin/BooksellerAuthTestPage';
import BooksellerEntitlementTestPage from './pages/admin/BooksellerEntitlementTestPage';
import ManageBooksPage from './pages/admin/ManageBooksPage';
import ManageLicensesPage from './pages/admin/ManageLicensesPage';
import ManagePublishersPage from './pages/admin/ManagePublishersPage';
import ManageBooksellersPage from './pages/admin/ManageBooksellersPage';
import ManageMissingBooksPage from './pages/admin/ManageMissingBooksPage';
import SettingsManagementPage from './pages/admin/SettingsManagementPage';
import TranslationManagementPage from './pages/admin/TranslationManagementPage';
import { BooksellerTtsConfigPage } from './pages/BooksellerTtsConfigPage';
import UnifiedLoginPage from './pages/UnifiedLoginPage';
import LoginCompletePage from './pages/LoginCompletePage';
import BooksellerDeepLinkPage from './pages/BooksellerDeepLinkPage';
import TTSComparisonPage from './prototypes/tts/TTSComparisonPage';
import { oidcConfig } from './config/auth.config';
import { AuthInitializer } from './components/AuthInitializer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AbpPermissionsProvider } from './contexts/AbpPermissionsContext';
import { AbpPermissions } from './constants/permissions';
import { TenantBrandingInitializer } from './components/TenantBrandingInitializer';
import { AppLoadingScreen } from './components/AppLoadingScreen';
import { TtsProvider } from './contexts/TtsContext';
import { AlertProvider } from './contexts/AlertContext';
import { CompactTTSPlayer } from './components/reader/CompactTTSPlayer';
import { UpdatePrompt } from './components/pwa/UpdatePrompt';
import { InstallPrompt } from './components/pwa/InstallPrompt';
import { Suspense } from 'react';
import './i18n/i18n';
import { LanguageProvider } from './i18n/LanguageContext';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <AuthProvider {...oidcConfig}>
      <AuthInitializer>
        <AbpPermissionsProvider>
          <TenantBrandingInitializer />
          <AppLoadingScreen>
            <QueryClientProvider client={queryClient}>
              <LanguageProvider>
              <Suspense fallback={null}>
              <BrowserRouter basename={import.meta.env.BASE_URL}>
                <AlertProvider>
                <TtsProvider>
                  <div className="min-h-screen bg-neutral-50">
                  {/* PWA Prompts */}
                  <UpdatePrompt />
                  <InstallPrompt />

                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route
                    path="/my-library"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ReadBook}>
                        <MyLibraryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-quotas"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ViewDrmQuotas}>
                        <StudentQuotasPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/upload"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.UploadBook}>
                        <BookUploadPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/read/:bookId"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ReadBook}>
                        <BookReaderPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/search"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ReadBook}>
                        <SearchResultsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/licenses/bulk-assign"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.BulkAssignLicenses}>
                        <BulkLicenseAssignmentPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/books"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManageBooks}>
                        <ManageBooksPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/publishers"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManagePublishers}>
                        <ManagePublishersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/booksellers"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManageBooksellers}>
                        <ManageBooksellersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/missing-books"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ViewMissingBooks}>
                        <ManageMissingBooksPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/licenses"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManageLicenses}>
                        <ManageLicensesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/test-bookseller-auth"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManageBooksellers}>
                        <BooksellerAuthTestPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/test-entitlement-api"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManageBooksellers}>
                        <BooksellerEntitlementTestPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tts-config"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManageBooksellerTts}>
                        <BooksellerTtsConfigPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <ProtectedRoute requirePermission={AbpPermissions.ManageSystemSettings}>
                        <SettingsManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/translations"
                    element={
                      <ProtectedRoute requirePermission="LanguageManagement.LanguageTexts">
                        <TranslationManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* TTS Prototype Testing */}
                  <Route path="/prototypes/tts" element={<TTSComparisonPage />} />
                  {/* Unified Authentication (Local + Bookseller) */}
                  <Route path="/login" element={<UnifiedLoginPage />} />
                  <Route path="/book/:slug/:booksellerBookId" element={<BooksellerDeepLinkPage />} />
                  {/* Legacy OIDC Callback (for LearnLeaf OAuth) */}
                  <Route path="/callback" element={<CallbackPage />} />
                  <Route path="/login/complete" element={<LoginCompletePage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                {/* Global TTS Player (shows when TTS is active) */}
                <CompactTTSPlayer />
              </div>
                </TtsProvider>
                </AlertProvider>
            </BrowserRouter>
              </Suspense>
              </LanguageProvider>
          </QueryClientProvider>
          </AppLoadingScreen>
        </AbpPermissionsProvider>
      </AuthInitializer>
    </AuthProvider>
  );
}

export default App;
